import React from "react";
import { motion } from "framer-motion";
import { Eye, Plus } from "lucide-react";
import { faviconFor, displayName } from "./listings";
import { goUrl } from "@/api";
import { RankGem } from "./RankGem";
import { ShareTrigger } from "./ShareCard";
import { listingOutboundHref } from "./shareLinks";

const Favicon = ({ listing, className }) => {
  const src = listing.favicon_url || listing.favicon || faviconFor(listing.url);
  if (!src) return null;
  return (
    <img
      data-testid="listing-favicon"
      src={src}
      alt=""
      width={16}
      height={16}
      className={`shrink-0 rounded-sm bg-white/10 ${className || ""}`}
    />
  );
};

const AmountClicks = ({ listing, amountCls }) => (
  <div className="flex flex-col items-center gap-1">
    <div className={`mono font-bold ${amountCls}`}>${listing.amount.toLocaleString()}</div>
    <div data-testid="listing-clicks" className="mono flex items-center gap-1 text-[10px] text-slate-500">
      <Eye size={11} /> {listing.clicks.toLocaleString()}
    </div>
  </div>
);

function defaultOpenListing(listing) {
  window.open(goUrl(listing.id), "_blank", "noopener,noreferrer");
}

const orderCls = (rank) => (rank === 1 ? "sm:order-2" : rank === 2 ? "sm:order-1" : "sm:order-3");

/** Compact gem cards for the hero (same chrome as the old full-width podium). */
export const Podium = ({ entry, rank, className = "", onOpen = defaultOpenListing }) => {
  const styles = {
    1: { cls: "podium-1 py-3 sm:py-8", num: "text-gem-amber bg-amber-500/10 border-amber-400/50", amount: "text-gem-amber text-xl sm:text-2xl" },
    2: { cls: "podium-2 py-3 sm:py-6", num: "text-gem-ruby bg-rose-500/10 border-rose-400/50", amount: "text-gem-ruby text-lg sm:text-xl" },
    3: { cls: "podium-3 py-3 sm:py-6", num: "text-gem-emerald bg-emerald-500/10 border-emerald-400/50", amount: "text-gem-emerald text-lg sm:text-xl" },
  }[rank];
  const desc = entry.description || "No description on this link yet.";
  const href = listingOutboundHref(entry.url);
  return (
    <motion.div
      layout
      id={`listing-${entry.id}`}
      data-testid={`podium-rank-${rank}`}
      onClick={() => onOpen(entry)}
      title={`Open ${displayName(entry)}`}
      className={`podium listing-card group relative flex min-w-0 cursor-pointer flex-col items-center px-1 text-center outline-none sm:px-3 ${styles.cls} ${className}`}
    >
      <RankGem rank={rank} className="mb-1.5 sm:mb-2" />
      <span className={`mono rank-badge border px-2 py-0.5 text-[10px] ${styles.num}`}>#{rank}</span>
      <div className="mt-1.5 flex w-full min-w-0 items-center justify-center gap-1.5 px-1 text-sm font-semibold text-white sm:mt-2 sm:text-base">
        <Favicon listing={entry} />
        {href ? (
          <a
            href={href}
            data-testid="listing-url"
            className="min-w-0 truncate max-w-full text-white hover:underline"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpen(entry);
            }}
          >
            {displayName(entry)}
          </a>
        ) : (
          <span className="min-w-0 truncate max-w-full">{displayName(entry)}</span>
        )}
        {entry.isYou && <span className="ml-1 inline shrink-0 text-gmh-cyan">★</span>}
      </div>
      <p className="listing-desc mt-1.5 line-clamp-2 w-full text-[11px] leading-relaxed text-slate-400">
        {desc}
      </p>
      <div className="mt-2 sm:mt-3">
        <AmountClicks listing={entry} amountCls={styles.amount} />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <ShareTrigger listing={entry} rank={rank} />
      </div>
    </motion.div>
  );
};

const GhostPodium = ({ rank, onClaim }) => (
  <div
    data-testid={`ghost-rank-${rank}`}
    className={`podium listing-card flex min-w-0 flex-col items-center border border-dashed border-[#26324e] bg-transparent px-1 py-3 text-center sm:px-3 ${orderCls(rank)}`}
  >
    <span className="mono rank-badge mb-1 border border-[#26324e] px-2 py-0.5 text-[10px] text-slate-600">
      #{rank}
    </span>
    <span className="mt-1 text-[11px] italic tracking-wide text-slate-600">open slot</span>
    <button
      type="button"
      data-testid={`ghost-claim-${rank}`}
      onClick={() => onClaim && onClaim(rank)}
      className="mono mt-2 flex items-center gap-1 rounded-md border border-[#1c2740] px-2 py-1.5 text-[10px] font-semibold tracking-wide text-slate-400 transition-colors hover:border-gmh-cyan/50 hover:text-gmh-cyan sm:mt-3 sm:px-3 sm:text-[11px]"
    >
      <Plus size={12} /> Take this spot
    </button>
  </div>
);

/** Hero podium: ranks 1–3 (paid cards or honest ghosts when the board is ready). */
export const PodiumStrip = ({ leaderboard, onOpenListing, onClaim, showGhosts = true }) => {
  const handleOpen = onOpenListing || defaultOpenListing;

  return (
    <div data-testid="podium-strip" className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3 sm:items-end">
      {[1, 2, 3].map((rank) => {
        const entry = leaderboard[rank - 1];
        if (entry) {
          return (
            <Podium
              key={entry.id}
              entry={entry}
              rank={rank}
              className={orderCls(rank)}
              onOpen={handleOpen}
            />
          );
        }
        if (!showGhosts) return null;
        return <GhostPodium key={`ghost-${rank}`} rank={rank} onClaim={onClaim} />;
      })}
    </div>
  );
};
